import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import graphql from "babel-plugin-relay/macro";
import classNames from "classnames";
import styles from "components/account/AccountDetailsForm.module.scss";
import { PersonNameSuffixes } from "components/account/PersonNameSuffixes";
import { PhoneNumber } from "components/phonenumber/PhoneNumber";
import { DatePicker } from "components/shared/datePicker/DatePicker";
import { ErrorMessage } from "components/shared/ErrorMessage";
import { FinishedLoadingNotification } from "components/shared/FinishedLoadingNotification";
import { accountMessages } from "domain/account/accountMessages";
import { IPhoneNumber, isPhoneNumberType } from "domain/account/models/phoneNumber";
import { IUpdateAccountInput, updateAccount } from "domain/account/updateAccount";
import { toastActions } from "domain/toast/toastActions";
import { AccountDetailsFormQuery, AccountDetailsFormQueryResponse } from "generatedQueries/AccountDetailsFormQuery.graphql";
import { updateAccountMutationResponse } from "generatedQueries/updateAccountMutation.graphql";
import { omit } from "lodash";
import moment from "moment";
import { FC } from "react";
import { IntlShape, MessageDescriptor, useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useLazyLoadQuery } from "react-relay";
import { Col, FormFeedback, Row } from "reactstrap";
import Button from "reactstrap/lib/Button";
import { FormLabel } from "shared/components/formInputs/FormLabel";
import formLabelStyles from "shared/components/formInputs/FormLabel.module.scss";
import { HideableContainer, HideableFields } from "shared/components/Hideable";
import { Message } from "shared/components/Message";
import { ErrorHandlingMutationForm } from "shared/components/newFormInputs/ErrorHandlingMutationForm";
import { FormFieldArray } from "shared/components/newFormInputs/FormFieldArray";
import { FormFieldErrors } from "shared/components/newFormInputs/FormFieldErrors";
import { TextBox } from "shared/components/newFormInputs/TextBox";
import { SiteCustomisation } from "shared/components/SiteCustomisation";
import { Switch } from "shared/components/Switch";
import { mapMessages } from "shared/mapMessages";
import { sharedMessages } from "shared/sharedMessages";
import { combine, isPastDate, maxDateOfBirth, maxLength, minDateOfBirth, required } from "shared/validations/validations";

const messages = mapMessages("components.account.AccountDetailsForm", {
    addPhoneNumberButton: "Add Phone Number",
});

interface IProps {
    onNext: () => void;
    isNewAccount: boolean;
    pageContentName: MessageDescriptor | string;
}

type AccountDetailsConfiguration = AccountDetailsFormQueryResponse["configuration"];

enum RequirableFields {
    dateOfBirth = "Account.DateOfBirth",
    driverLicence = "Account.DriverLicence",
}

export const AccountDetailsForm: FC<IProps> = ({
    onNext,
    isNewAccount,
    pageContentName,
}) => {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();

    const { account, configuration, features, isSeniorCitizenMessage, isSeniorCitizenLabel, ...fragments } = useLazyLoadQuery<AccountDetailsFormQuery>(
        query,
        {
            suffixesPageSize: 50,
            suffixesWhere: {
                isShown: true,
            },
        });

    if (!account || !configuration) {
        return <ErrorMessage message={sharedMessages.requestFailedBody} heading={sharedMessages.requestFailedTitle} />;
    }

    const shouldDisplayIsSeniorCheckbox = !!features && features.isSelfIdentifyAsSeniorCitizenEnabled;
    const isDriverLicenceRequired = isRequired(configuration, RequirableFields.driverLicence);
    const isDateOfBirthRequired = isRequired(configuration, RequirableFields.dateOfBirth);
    const seniorCitizenLabel = isSeniorCitizenLabel?.value ?
        <Message message={accountMessages.isCustomSeniorCitizenCheckboxLabel} values={{ personType: isSeniorCitizenLabel?.value }} /> :
        <Message message={accountMessages.isSeniorCitizenCheckboxLabel} />;
    const defaultValues = getInitialValues(account, shouldDisplayIsSeniorCheckbox);

    return <Row className="justify-content-center" aria-busy="false">
        <Col xl={5} lg={6} md={7} sm={9}>
            <FinishedLoadingNotification contentName={pageContentName} />
            <ErrorHandlingMutationForm<IUpdateAccountInput, updateAccountMutationResponse>
                onNext={onSuccess}
                defaultValues={defaultValues}
                saveButtonMessage={account.status === "NEW" ? accountMessages.createAccountLabel : sharedMessages.saveLabel}
                mutationFunc={updateAccount}
                alwaysSubmit={isNewAccount}>
                {({ control, register }) => (
                    <>
                        <FormLabel
                            message={accountMessages.firstNameLabel}
                            required={true}
                            for="firstName"
                        >
                            <TextBox
                                id="firstName"
                                name="firstName"
                                placeholder={formatMessage(accountMessages.firstNameLabel)}
                                control={control}
                                register={register}
                                options={combine<IUpdateAccountInput, "firstName">(
                                    required(),
                                    maxLength(100)
                                )}
                                aria-required={true} />
                        </FormLabel>
                        <FormLabel
                            message={accountMessages.middleNameLabel}
                            for="middleName"
                        >
                            <TextBox
                                id="middleName"
                                name="middleName"
                                placeholder={formatMessage(accountMessages.middleNameLabel)}
                                control={control}
                                register={register}
                                options={maxLength<IUpdateAccountInput, "middleName">(100)} />
                        </FormLabel>
                        <FormLabel
                            message={accountMessages.lastNameLabel}
                            required={true}
                            for="lastName"
                        >
                            <TextBox
                                id="lastName"
                                name="lastName"
                                placeholder={formatMessage(accountMessages.lastNameLabel)}
                                control={control}
                                register={register}
                                options={combine<IUpdateAccountInput, "lastName">(
                                    required(),
                                    maxLength(100))}
                                aria-required={true} />
                        </FormLabel>
                        <FormLabel message={accountMessages.personNameSuffixLabel} for="personNameSuffixId">
                            <PersonNameSuffixes
                                name="personNameSuffixId"
                                queryKey={fragments}
                            />
                        </FormLabel>
                        <HideableContainer field={HideableFields.driverLicence} configuration={configuration}>
                            <FormLabel
                                message={accountMessages.driverLicenceLabel}
                                required={isDriverLicenceRequired}
                                for="driverLicence"
                            >
                                <TextBox
                                    id="driverLicence"
                                    name="driverLicence"
                                    placeholder={formatMessage(accountMessages.driverLicenceLabel)}
                                    control={control}
                                    register={register}
                                    options={isDriverLicenceRequired
                                        ? combine<IUpdateAccountInput, "driverLicence">(required(), maxLength(20))
                                        : maxLength<IUpdateAccountInput, "driverLicence">(20)
                                    }
                                    aria-required={isDriverLicenceRequired} />
                            </FormLabel>
                        </HideableContainer>
                        {
                            !shouldDisplayIsSeniorCheckbox &&
                            <HideableContainer field={HideableFields.dateOfBirth} configuration={configuration}>
                                <FormLabel
                                    message={accountMessages.dateOfBirthLabel}
                                    required={isDateOfBirthRequired}
                                    for="dateOfBirth"
                                >
                                    <DatePicker
                                        id="dateOfBirth"
                                        name="dateOfBirth"
                                        maxDate={moment().add(-1, "days").toDate()}
                                        rules={isDateOfBirthRequired
                                            ? combine<IUpdateAccountInput, "dateOfBirth">(required(), isPastDate(formatMessage), minDateOfBirth(formatMessage), maxDateOfBirth(formatMessage))
                                            : combine<IUpdateAccountInput, "dateOfBirth">(isPastDate(formatMessage), minDateOfBirth(formatMessage), maxDateOfBirth(formatMessage))}
                                        required={isDateOfBirthRequired}
                                    />
                                </FormLabel>
                            </HideableContainer>
                        }
                        <FormFieldErrors name="phoneNumbers">
                            {(phoneNumberErrors) => (
                                <fieldset
                                    className={classNames(styles.phoneNumberFieldset, "row", "form-group")}
                                    aria-labelledby="phoneNumbersLabel"
                                    aria-invalid={!!phoneNumberErrors?.root}
                                    aria-errormessage={!!phoneNumberErrors?.root ? "phoneNumbers-errors" : undefined }>
                                    <legend id="phoneNumbersLabel" className={classNames(formLabelStyles.label, "col-12 col-form-label")}>
                                        <Message message={accountMessages.phoneNumbersLabel} />
                                        <span aria-hidden="true" className={formLabelStyles.required}> *</span>
                                    </legend>
                                    <div className="col-12">
                                        <FormFieldArray name="phoneNumbers" rules={{ validate: duplicatePhoneNumberTypeValidator(formatMessage) }}>
                                            {({ fields, append, remove }) =>
                                                <>
                                                    {
                                                        fields.map((field, index) => (
                                                            <PhoneNumber
                                                                key={field.id}
                                                                formFieldArrayName={"phoneNumbers"}
                                                                index={index}
                                                                shouldRenderRemoveButton={fields.length > 1}
                                                                onRemoveClicked={() => remove(index)}
                                                                configurationRef={configuration}
                                                            />
                                                        ))
                                                    }
                                                    {
                                                        <Button
                                                            color="success"
                                                            onClick={() => append({ type: null, number: "" })}
                                                            aria-label={formatMessage(messages.addPhoneNumberButton)}
                                                            size="sm"
                                                        >
                                                            <FontAwesomeIcon icon={faPlus} className="icon-button mr-1" />
                                                            <Message message={accountMessages.addPhoneNumberLabel} />
                                                        </Button>
                                                    }
                                                </>
                                            }
                                        </FormFieldArray>
                                        <FormFeedback
                                            id="phoneNumbers-errors"
                                            className={phoneNumberErrors?.root ? "d-block" : "d-none"}
                                            role="alert"
                                            >
                                                {phoneNumberErrors?.root?.message}
                                        </FormFeedback>
                                    </div>
                                </fieldset>
                            )}
                        </FormFieldErrors>
                        {
                            shouldDisplayIsSeniorCheckbox &&
                            <Switch
                                label={seniorCitizenLabel}
                                id="isSelfIdentifiedAsSeniorCitizen"
                                className="mt-3"
                                {...register("isSelfIdentifiedAsSeniorCitizen")}
                            />
                        }
                        {
                            shouldDisplayIsSeniorCheckbox &&
                            isSeniorCitizenMessage &&
                            <SiteCustomisation value={isSeniorCitizenMessage.value} />
                        }
                </>
            )}
            </ErrorHandlingMutationForm>
        </Col>
    </Row>;

    function onSuccess() {
        dispatch(toastActions.toastSuccess());
        onNext();
    }

    function getInitialValues(account: Exclude<AccountDetailsFormQueryResponse["account"], null>, shouldCollectIsSeniorCitizen: boolean): IUpdateAccountInput {
        const phoneNumbers = account.phoneNumbers.map(p => {
            return { ...p };
        });

        if (phoneNumbers.length === 0) {
            phoneNumbers.push({
                number: "",
                type: "HOME",
            });
        }

        return {
            ...omit(account, "id", "status"),
            isSelfIdentifiedAsSeniorCitizen: account.isSelfIdentifiedAsSeniorCitizen !== null ? account.isSelfIdentifiedAsSeniorCitizen : (shouldCollectIsSeniorCitizen ? false : null),
            phoneNumbers,
        };
    }

    function isRequired(configuration: AccountDetailsConfiguration, field: string) {
        return configuration !== null && (configuration.requiredFields === null || configuration.requiredFields === undefined || configuration.requiredFields.split(",").includes(field));
    }
};

function duplicatePhoneNumberTypeValidator(formatMessage: IntlShape["formatMessage"]) {
    return (values: unknown[]) => {
        const phoneNumberTypes = values.filter(isPhoneNumberField).map(phoneNumber => phoneNumber.type);
        const uniquePhoneNumberTypes = new Set(phoneNumberTypes);
        if (phoneNumberTypes.length !== uniquePhoneNumberTypes.size) {
            return formatMessage(accountMessages.duplicatePhoneTypeError);
        }

        return undefined;
    };

    function isPhoneNumberField(value: unknown): value is IPhoneNumber {
        if (typeof value !== "object" || !value) {
            return false;
        }

        const phoneNumber = value as IPhoneNumber;
        return phoneNumber.number !== undefined && phoneNumber.type !== undefined && isPhoneNumberType(phoneNumber.type);
    }
}

const query = graphql`
    query AccountDetailsFormQuery(
        $suffixesPageSize: Int,
        $suffixesAfter: String,
        $suffixesWhere: PersonNameSuffixFilter,
    ) {
        account {
            id
            status
            firstName
            middleName
            lastName
            personNameSuffixId
            dateOfBirth
            isSelfIdentifiedAsSeniorCitizen
            driverLicence
            phoneNumbers {
                type
                number
            }
        }
        configuration {
            ...Hideable_configuration
            ...PhoneNumber_configuration
            requiredFields
        }
        features {
            isSelfIdentifyAsSeniorCitizenEnabled
        }
        isSeniorCitizenMessage: siteCustomisation(application: "licensing", name: "isSeniorCitizenMessage") {
            value
        }
        isSeniorCitizenLabel: siteCustomisation(application: "licensing", name: "isSeniorCitizenLabel") {
            value
        }
        ...PersonNameSuffixes_query
    }`;
